import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  Clock3,
  Inbox,
  Plus,
  Save,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Trash2,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';
import { api } from '../../api';
import './queue-routing-admin.css';

type QueueMember = { id: string; full_name: string; email?: string; role?: string };
type QueueRow = {
  id: string;
  name: string;
  description?: string | null;
  color: string;
  is_default: boolean;
  is_active: boolean;
  active_tickets?: number;
  members_count?: number;
  members?: QueueMember[];
};
type Company = { id: string; name: string; is_active?: boolean };
type RoutingRule = {
  id?: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  isFallback: boolean;
  channels: string[];
  companyIds: string[];
  priorities: string[];
  categories: string[];
  keywords: string[];
  weekdays: number[];
  timeFrom?: string | null;
  timeTo?: string | null;
  targetQueueId: string;
  targetQueueName?: string;
  targetQueueColor?: string;
  targetAssigneeId?: string | null;
  targetAssigneeName?: string | null;
};
type RuleSet = { id: string; revision: number; name: string; status: string; publishedAt?: string | null; rules: RoutingRule[] };
type RoutingWorkspace = {
  draft: RuleSet;
  published: RuleSet | null;
  history: Array<{ id: string; revision: number; status: string; publishedAt?: string; publishedByName?: string; rulesCount: number }>;
};
type QueueDraft = { name: string; description: string; color: string; isDefault: boolean; isActive: boolean };
type PreviewResult = { matched: boolean; ruleName?: string | null; queueName?: string | null; assigneeName?: string | null };

const CHANNELS = [
  { value: 'PORTAL', label: 'Портал' },
  { value: 'EMAIL', label: 'Почта' },
  { value: 'PHONE', label: 'Телефон' },
  { value: 'TELEGRAM', label: 'Telegram' },
  { value: 'MAX', label: 'MAX' },
];
const PRIORITIES = [
  { value: 'CRITICAL', label: 'Критичный' },
  { value: 'HIGH', label: 'Высокий' },
  { value: 'NORMAL', label: 'Обычный' },
  { value: 'LOW', label: 'Низкий' },
];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function blankQueue(): QueueDraft {
  return { name: '', description: '', color: '#1769e0', isDefault: false, isActive: true };
}

function queueDraft(queue?: QueueRow): QueueDraft {
  return queue ? {
    name: queue.name,
    description: queue.description || '',
    color: queue.color,
    isDefault: queue.is_default,
    isActive: queue.is_active,
  } : blankQueue();
}

function blankRule(queueId = ''): RoutingRule {
  return {
    name: 'Новое правило', description: '', sortOrder: 0, isActive: true, isFallback: false,
    channels: [], companyIds: [], priorities: [], categories: [], keywords: [], weekdays: [],
    timeFrom: null, timeTo: null, targetQueueId: queueId, targetAssigneeId: null,
  };
}

function cloneWorkspaceDraft(value?: RuleSet) {
  return value ? { ...value, rules: value.rules.map(rule => ({ ...rule, channels: [...rule.channels], companyIds: [...rule.companyIds], priorities: [...rule.priorities], categories: [...rule.categories], keywords: [...rule.keywords], weekdays: [...rule.weekdays] })) } : null;
}

function formatDate(value?: string | null) {
  if (!value) return 'ещё не публиковалась';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function conditions(rule: RoutingRule, companies: Company[]) {
  if (rule.isFallback) return ['Любое обращение, не совпавшее с правилами выше'];
  const values: string[] = [];
  if (rule.channels.length) values.push(rule.channels.map(value => CHANNELS.find(item => item.value === value)?.label || value).join(', '));
  if (rule.priorities.length) values.push(rule.priorities.map(value => PRIORITIES.find(item => item.value === value)?.label || value).join(', '));
  if (rule.companyIds.length) values.push(rule.companyIds.map(id => companies.find(company => company.id === id)?.name || 'Компания').join(', '));
  if (rule.categories.length) values.push(`Категории: ${rule.categories.join(', ')}`);
  if (rule.keywords.length) values.push(`Слова: ${rule.keywords.join(', ')}`);
  if (rule.weekdays.length) values.push(rule.weekdays.map(day => WEEKDAYS[day - 1]).join(', '));
  if (rule.timeFrom && rule.timeTo) values.push(`${rule.timeFrom}–${rule.timeTo}`);
  return values.length ? values : ['Все обращения'];
}

function ToggleGroup({ options, value, onChange, disabled = false }: { options: Array<{ value: string; label: string }>; value: string[]; onChange: (value: string[]) => void; disabled?: boolean }) {
  return <div className="routing-toggle-group">
    {options.map(option => <button key={option.value} type="button" disabled={disabled} className={value.includes(option.value) ? 'active' : ''} onClick={() => onChange(value.includes(option.value) ? value.filter(item => item !== option.value) : [...value, option.value])}>{value.includes(option.value) && <Check />}{option.label}</button>)}
  </div>;
}

export function QueueRoutingAdmin() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'teams' | 'routing'>('teams');
  const [selectedQueueId, setSelectedQueueId] = useState('');
  const [creatingQueue, setCreatingQueue] = useState(false);
  const [queueForm, setQueueForm] = useState<QueueDraft>(blankQueue());
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [draft, setDraft] = useState<RuleSet | null>(null);
  const [selectedRuleIndex, setSelectedRuleIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState({ channel: 'EMAIL', companyId: '', priority: 'NORMAL', category: '', subject: '', description: '' });

  const queuesQuery = useQuery({ queryKey: ['queues'], queryFn: () => api<QueueRow[]>('/queues') });
  const membersQuery = useQuery({ queryKey: ['queue-member-catalog'], queryFn: () => api<QueueMember[]>('/queues/members/catalog') });
  const companiesQuery = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies') });
  const routingQuery = useQuery({ queryKey: ['queue-routing-workspace'], queryFn: () => api<RoutingWorkspace>('/queues/routing/workspace') });
  const queues = queuesQuery.data || [];
  const people = membersQuery.data || [];
  const companies = (companiesQuery.data || []).filter(company => company.is_active !== false);
  const selectedQueue = queues.find(queue => queue.id === selectedQueueId);
  const selectedRule = draft?.rules[selectedRuleIndex];

  useEffect(() => {
    if (!queues.length) return;
    if (!selectedQueueId || !queues.some(queue => queue.id === selectedQueueId)) setSelectedQueueId(queues[0].id);
  }, [queues, selectedQueueId]);

  useEffect(() => {
    if (!selectedQueue || creatingQueue) return;
    setQueueForm(queueDraft(selectedQueue));
    setSelectedMembers((selectedQueue.members || []).map(member => member.id));
  }, [selectedQueue, creatingQueue]);

  useEffect(() => {
    if (!routingQuery.data?.draft) return;
    setDraft(cloneWorkspaceDraft(routingQuery.data.draft));
    setSelectedRuleIndex(index => Math.min(index, Math.max(0, routingQuery.data.draft.rules.length - 1)));
  }, [routingQuery.data]);

  const queueMutation = useMutation({
    mutationFn: () => api<QueueRow>(creatingQueue ? '/queues' : `/queues/${selectedQueueId}`, {
      method: creatingQueue ? 'POST' : 'PATCH',
      body: JSON.stringify(queueForm),
    }),
    onSuccess: async saved => {
      setError(''); setFeedback(creatingQueue ? 'Очередь создана' : 'Параметры очереди сохранены');
      setCreatingQueue(false); setSelectedQueueId(saved.id || selectedQueueId);
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
    onError: cause => setError(cause instanceof Error ? cause.message : 'Не удалось сохранить очередь'),
  });
  const membersMutation = useMutation({
    mutationFn: () => api(`/queues/${selectedQueueId}/members`, { method: 'PUT', body: JSON.stringify({ userIds: selectedMembers }) }),
    onSuccess: async () => { setError(''); setFeedback('Состав команды обновлён'); await queryClient.invalidateQueries({ queryKey: ['queues'] }); },
    onError: cause => setError(cause instanceof Error ? cause.message : 'Не удалось обновить команду'),
  });
  const saveRouting = useMutation({
    mutationFn: () => api<RoutingWorkspace>('/queues/routing/draft', {
      method: 'PUT',
      body: JSON.stringify({ name: draft?.name || 'Маршрутизация заявок', rules: draft?.rules || [] }),
    }),
    onSuccess: data => { setError(''); setFeedback('Черновик сохранён'); queryClient.setQueryData(['queue-routing-workspace'], data); },
    onError: cause => setError(cause instanceof Error ? cause.message : 'Не удалось сохранить правила'),
  });
  const publishRouting = useMutation({
    mutationFn: () => api<RoutingWorkspace>('/queues/routing/publish', { method: 'POST' }),
    onSuccess: data => { setError(''); setFeedback('Новая версия маршрутизации опубликована'); queryClient.setQueryData(['queue-routing-workspace'], data); },
    onError: cause => setError(cause instanceof Error ? cause.message : 'Не удалось опубликовать правила'),
  });
  const previewMutation = useMutation({
    mutationFn: () => api<PreviewResult>('/queues/routing/preview', { method: 'POST', body: JSON.stringify({ ...preview, companyId: preview.companyId || undefined }) }),
    onError: cause => setError(cause instanceof Error ? cause.message : 'Не удалось проверить маршрут'),
  });

  const filteredPeople = useMemo(() => {
    const needle = memberSearch.trim().toLowerCase();
    return people.filter(person => !needle || `${person.full_name} ${person.email || ''}`.toLowerCase().includes(needle));
  }, [people, memberSearch]);

  const updateRule = (patch: Partial<RoutingRule>) => {
    setDraft(current => current ? { ...current, rules: current.rules.map((rule, index) => index === selectedRuleIndex ? { ...rule, ...patch } : rule) } : current);
  };
  const addRule = () => {
    setDraft(current => {
      if (!current) return current;
      const fallbackIndex = current.rules.findIndex(rule => rule.isFallback);
      const index = fallbackIndex >= 0 ? fallbackIndex : current.rules.length;
      const rules = [...current.rules];
      rules.splice(index, 0, { ...blankRule(queues.find(queue => queue.is_default)?.id || queues[0]?.id || ''), sortOrder: index });
      setSelectedRuleIndex(index);
      return { ...current, rules };
    });
  };
  const moveRule = (direction: -1 | 1) => {
    if (!draft) return;
    const target = selectedRuleIndex + direction;
    if (target < 0 || target >= draft.rules.length || selectedRule?.isFallback) return;
    const rules = [...draft.rules];
    if (rules[target]?.isFallback) return;
    [rules[selectedRuleIndex], rules[target]] = [rules[target], rules[selectedRuleIndex]];
    setDraft({ ...draft, rules }); setSelectedRuleIndex(target);
  };
  const removeRule = () => {
    if (!draft || !selectedRule || selectedRule.isFallback) return;
    const rules = draft.rules.filter((_, index) => index !== selectedRuleIndex);
    setDraft({ ...draft, rules }); setSelectedRuleIndex(Math.max(0, selectedRuleIndex - 1));
  };
  const targetMembers = queues.find(queue => queue.id === selectedRule?.targetQueueId)?.members || [];

  return <div className="queue-routing-admin">
    <header className="queue-admin-hero">
      <div><span><Workflow /></span><div><small>ЦЕНТР РАСПРЕДЕЛЕНИЯ</small><h2>Очереди и маршрутизация</h2><p>Команды, приоритеты и автоматическое назначение обращений из всех каналов</p></div></div>
      <nav aria-label="Режим управления очередями">
        <button type="button" className={mode === 'teams' ? 'active' : ''} onClick={() => setMode('teams')}><Users /> Команды</button>
        <button type="button" className={mode === 'routing' ? 'active' : ''} onClick={() => setMode('routing')}><Workflow /> Маршрутизация</button>
      </nav>
    </header>

    {feedback && <div className="queue-admin-message success"><CheckCircle2 /> {feedback}<button type="button" onClick={() => setFeedback('')}>×</button></div>}
    {error && <div className="queue-admin-message error"><AlertTriangle /> {error}<button type="button" onClick={() => setError('')}>×</button></div>}

    {mode === 'teams' && <div className="queue-teams-layout">
      <section className="queue-directory-panel">
        <div className="queue-panel-title"><div><Inbox /><span><b>Команды</b><small>{queues.filter(queue => queue.is_active).length} активных очередей</small></span></div><button type="button" onClick={() => { setCreatingQueue(true); setSelectedQueueId(''); setQueueForm(blankQueue()); setSelectedMembers([]); }}><Plus /> Новая</button></div>
        <div className="queue-directory-list">
          {queues.map(queue => <button type="button" key={queue.id} className={!creatingQueue && queue.id === selectedQueueId ? 'active' : ''} onClick={() => { setCreatingQueue(false); setSelectedQueueId(queue.id); }}>
            <i style={{ background: queue.color }} /><span><strong>{queue.name}</strong><small>{queue.description || 'Без описания'}</small></span>
            <em>{queue.active_tickets || 0}<small>заявок</small></em><b>{queue.members_count || 0}<small>чел.</small></b>
          </button>)}
        </div>
      </section>

      <section className="queue-editor-panel">
        <div className="queue-editor-heading"><div style={{ '--queue-color': queueForm.color } as CSSProperties}><Settings /></div><span><small>{creatingQueue ? 'НОВАЯ ОЧЕРЕДЬ' : selectedQueue?.is_default ? 'ОЧЕРЕДЬ ПО УМОЛЧАНИЮ' : 'НАСТРОЙКА КОМАНДЫ'}</small><h3>{creatingQueue ? 'Создание очереди' : selectedQueue?.name || 'Выберите очередь'}</h3></span></div>
        <div className="queue-editor-grid">
          <label><span>Название</span><input value={queueForm.name} onChange={event => setQueueForm(value => ({ ...value, name: event.target.value }))} placeholder="Например: Инфраструктура" /></label>
          <label className="queue-color-field"><span>Цвет</span><div><input type="color" value={queueForm.color} onChange={event => setQueueForm(value => ({ ...value, color: event.target.value }))} /><code>{queueForm.color.toUpperCase()}</code></div></label>
          <label className="wide"><span>Назначение очереди</span><textarea rows={3} value={queueForm.description} onChange={event => setQueueForm(value => ({ ...value, description: event.target.value }))} placeholder="Какие обращения обрабатывает эта команда" /></label>
        </div>
        <div className="queue-setting-switches">
          <label><span><b>Активная очередь</b><small>Принимает новые обращения</small></span><input type="checkbox" checked={queueForm.isActive} onChange={event => setQueueForm(value => ({ ...value, isActive: event.target.checked }))} /></label>
          <label><span><b>По умолчанию</b><small>Резервное направление портала</small></span><input type="checkbox" checked={queueForm.isDefault} onChange={event => setQueueForm(value => ({ ...value, isDefault: event.target.checked }))} /></label>
        </div>
        <button type="button" className="queue-save-button" disabled={!queueForm.name.trim() || queueMutation.isPending} onClick={() => queueMutation.mutate()}><Save /> {queueMutation.isPending ? 'Сохраняем…' : creatingQueue ? 'Создать очередь' : 'Сохранить параметры'}</button>

        {!creatingQueue && selectedQueue && <section className="queue-member-editor">
          <div className="queue-member-head"><div><Users /><span><b>Состав команды</b><small>Сотрудники, которые видят и обрабатывают очередь</small></span></div><em>{selectedMembers.length}</em></div>
          <label className="queue-member-search"><Search /><input value={memberSearch} onChange={event => setMemberSearch(event.target.value)} placeholder="Найти сотрудника" /></label>
          <div className="queue-member-list">{filteredPeople.map(person => <label key={person.id} className={selectedMembers.includes(person.id) ? 'selected' : ''}><input type="checkbox" checked={selectedMembers.includes(person.id)} onChange={() => setSelectedMembers(value => value.includes(person.id) ? value.filter(id => id !== person.id) : [...value, person.id])} /><span>{person.full_name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()}</span><p><b>{person.full_name}</b><small>{person.email || person.role || 'Сотрудник'}</small></p><Check /></label>)}</div>
          <button type="button" className="queue-member-save" onClick={() => membersMutation.mutate()} disabled={membersMutation.isPending}><Users /> {membersMutation.isPending ? 'Обновляем…' : 'Сохранить состав'}</button>
        </section>}
      </section>
    </div>}

    {mode === 'routing' && <div className="routing-workspace">
      <header className="routing-release-bar">
        <div className={routingQuery.data?.published ? 'published' : ''}><ShieldCheck /><span><small>РАБОЧАЯ КОНФИГУРАЦИЯ</small><b>{routingQuery.data?.published ? `Опубликована версия ${routingQuery.data.published.revision}` : 'Правила ещё не опубликованы'}</b><em>{formatDate(routingQuery.data?.published?.publishedAt)}</em></span></div>
        <div className="routing-release-actions"><span><Clock3 /> Черновик {draft?.revision || '—'}</span><button type="button" onClick={() => saveRouting.mutate()} disabled={!draft || saveRouting.isPending}><Save /> {saveRouting.isPending ? 'Сохраняем…' : 'Сохранить черновик'}</button><button type="button" className="publish" onClick={() => publishRouting.mutate()} disabled={!draft || publishRouting.isPending}><Send /> {publishRouting.isPending ? 'Публикуем…' : 'Опубликовать правила'}</button></div>
      </header>

      <div className="routing-main-grid">
        <section className="routing-rule-list-panel">
          <div className="queue-panel-title"><div><Workflow /><span><b>Приоритет правил</b><small>Проверяются сверху вниз</small></span></div><button type="button" onClick={addRule}><Plus /> Правило</button></div>
          <div className="routing-rule-list">{draft?.rules.map((rule, index) => <button type="button" key={rule.id || `${rule.name}-${index}`} className={selectedRuleIndex === index ? 'active' : ''} onClick={() => setSelectedRuleIndex(index)}>
            <i style={{ background: queues.find(queue => queue.id === rule.targetQueueId)?.color || '#7d8ba0' }} /><em>{rule.isFallback ? '↳' : index + 1}</em><span><strong>{rule.name}</strong><small>{conditions(rule, companies).slice(0, 2).join(' · ')}</small><b>{rule.targetQueueName || queues.find(queue => queue.id === rule.targetQueueId)?.name || 'Очередь не выбрана'}</b></span>{!rule.isActive && <mark>Выкл.</mark>}
          </button>)}</div>
          <div className="routing-order-note"><Zap /><span><b>Первое совпадение</b><small>После совпадения остальные правила не проверяются.</small></span></div>
        </section>

        <section className="routing-rule-editor">
          {selectedRule ? <>
            <div className="routing-rule-editor-head"><div><span>{selectedRule.isFallback ? <ShieldCheck /> : <Workflow />}</span><p><small>{selectedRule.isFallback ? 'РЕЗЕРВНЫЙ МАРШРУТ' : `ПРАВИЛО ${selectedRuleIndex + 1}`}</small><b>{selectedRule.name}</b></p></div><aside><button type="button" title="Выше" onClick={() => moveRule(-1)} disabled={selectedRuleIndex === 0 || selectedRule.isFallback}><ArrowUp /></button><button type="button" title="Ниже" onClick={() => moveRule(1)} disabled={selectedRule.isFallback || draft?.rules[selectedRuleIndex + 1]?.isFallback}><ArrowDown /></button><button type="button" className="danger" title="Удалить" onClick={removeRule} disabled={selectedRule.isFallback}><Trash2 /></button></aside></div>
            <div className="routing-rule-fields">
              <label><span>Название правила</span><input value={selectedRule.name} onChange={event => updateRule({ name: event.target.value })} /></label>
              <label><span>Описание</span><input value={selectedRule.description || ''} onChange={event => updateRule({ description: event.target.value })} placeholder="Для чего используется правило" /></label>
            </div>
            <div className="routing-rule-switches"><label><span><b>Правило активно</b><small>Участвует в маршрутизации после публикации</small></span><input type="checkbox" checked={selectedRule.isActive} onChange={event => updateRule({ isActive: event.target.checked })} /></label><label><span><b>Резервное правило</b><small>Срабатывает, если выше нет совпадений</small></span><input type="checkbox" checked={selectedRule.isFallback} disabled={selectedRule.isFallback} onChange={event => updateRule({ isFallback: event.target.checked, channels: [], companyIds: [], priorities: [], categories: [], keywords: [], weekdays: [], timeFrom: null, timeTo: null })} /></label></div>

            {!selectedRule.isFallback && <div className="routing-conditions">
              <div className="routing-subhead"><FilterIcon /><span><b>Условия обращения</b><small>Пустое поле означает «любое значение»</small></span></div>
              <label><span>Каналы</span><ToggleGroup options={CHANNELS} value={selectedRule.channels} onChange={channels => updateRule({ channels })} /></label>
              <label><span>Приоритет</span><ToggleGroup options={PRIORITIES} value={selectedRule.priorities} onChange={priorities => updateRule({ priorities })} /></label>
              <div className="routing-condition-grid"><label><span>Категории через запятую</span><input value={selectedRule.categories.join(', ')} onChange={event => updateRule({ categories: event.target.value.split(',').map(value => value.trim()).filter(Boolean) })} placeholder="1С, Сеть, Доступы" /></label><label><span>Ключевые слова</span><input value={selectedRule.keywords.join(', ')} onChange={event => updateRule({ keywords: event.target.value.split(',').map(value => value.trim()).filter(Boolean) })} placeholder="сервер, авария" /></label></div>
              <label><span>Компании</span><div className="routing-company-picker"><select value="" onChange={event => { if (event.target.value && !selectedRule.companyIds.includes(event.target.value)) updateRule({ companyIds: [...selectedRule.companyIds, event.target.value] }); }}><option value="">Добавить компанию…</option>{companies.filter(company => !selectedRule.companyIds.includes(company.id)).map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select><div>{selectedRule.companyIds.map(id => <button type="button" key={id} onClick={() => updateRule({ companyIds: selectedRule.companyIds.filter(value => value !== id) })}>{companies.find(company => company.id === id)?.name || 'Компания'} ×</button>)}</div></div></label>
              <label><span>Дни недели</span><div className="routing-weekdays">{WEEKDAYS.map((day, index) => <button type="button" key={day} className={selectedRule.weekdays.includes(index + 1) ? 'active' : ''} onClick={() => updateRule({ weekdays: selectedRule.weekdays.includes(index + 1) ? selectedRule.weekdays.filter(value => value !== index + 1) : [...selectedRule.weekdays, index + 1] })}>{day}</button>)}</div></label>
              <div className="routing-time-window"><label><span>С</span><input type="time" value={selectedRule.timeFrom || ''} onChange={event => updateRule({ timeFrom: event.target.value || null, timeTo: event.target.value && !selectedRule.timeTo ? '23:59' : selectedRule.timeTo })} /></label><label><span>До</span><input type="time" value={selectedRule.timeTo || ''} onChange={event => updateRule({ timeTo: event.target.value || null, timeFrom: event.target.value && !selectedRule.timeFrom ? '00:00' : selectedRule.timeFrom })} /></label><button type="button" onClick={() => updateRule({ timeFrom: null, timeTo: null })}>Круглосуточно</button></div>
            </div>}

            <div className="routing-destination">
              <div className="routing-subhead"><Inbox /><span><b>Куда направить</b><small>Очередь и необязательный основной исполнитель</small></span></div>
              <div><label><span>Целевая очередь</span><select value={selectedRule.targetQueueId} onChange={event => updateRule({ targetQueueId: event.target.value, targetQueueName: queues.find(queue => queue.id === event.target.value)?.name, targetAssigneeId: null, targetAssigneeName: null })}>{queues.filter(queue => queue.is_active).map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}</select></label><label><span>Ответственный</span><select value={selectedRule.targetAssigneeId || ''} onChange={event => updateRule({ targetAssigneeId: event.target.value || null, targetAssigneeName: targetMembers.find(member => member.id === event.target.value)?.full_name || null })}><option value="">Назначает диспетчер</option>{targetMembers.map(member => <option key={member.id} value={member.id}>{member.full_name}</option>)}</select></label></div>
            </div>
          </> : <div className="routing-editor-empty"><Workflow /><b>Добавьте правило</b><span>Укажите условия и направление обращения.</span></div>}
        </section>
      </div>

      <section className="routing-preview-panel">
        <div className="routing-preview-copy"><span><Search /></span><div><small>БЕЗ СОЗДАНИЯ ЗАЯВКИ</small><h3>Проверка маршрута</h3><p>Заполните пример обращения и убедитесь, куда попадёт заявка после публикации черновика.</p></div></div>
        <form onSubmit={(event: FormEvent) => { event.preventDefault(); previewMutation.mutate(); }}>
          <select value={preview.channel} onChange={event => setPreview(value => ({ ...value, channel: event.target.value }))}>{CHANNELS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
          <select value={preview.priority} onChange={event => setPreview(value => ({ ...value, priority: event.target.value }))}>{PRIORITIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
          <select value={preview.companyId} onChange={event => setPreview(value => ({ ...value, companyId: event.target.value }))}><option value="">Любая компания</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select>
          <input value={preview.subject} onChange={event => setPreview(value => ({ ...value, subject: event.target.value }))} placeholder="Тема обращения" required />
          <button type="submit" disabled={previewMutation.isPending}><Search /> {previewMutation.isPending ? 'Проверяем…' : 'Проверить'}</button>
        </form>
        {previewMutation.data && <div className={`routing-preview-result ${previewMutation.data.matched ? 'matched' : ''}`}><CheckCircle2 /><span><small>{previewMutation.data.matched ? 'МАРШРУТ НАЙДЕН' : 'НЕТ СОВПАДЕНИЯ'}</small><b>{previewMutation.data.ruleName || 'Ни одно правило не сработало'}</b><em>{previewMutation.data.queueName || 'Очередь не выбрана'}{previewMutation.data.assigneeName ? ` · ${previewMutation.data.assigneeName}` : ''}</em></span></div>}
      </section>

      {!!routingQuery.data?.history.length && <section className="routing-history-strip"><div><Clock3 /><span><b>История публикаций</b><small>Последние рабочие версии</small></span></div>{routingQuery.data.history.slice(0, 5).map(item => <article key={item.id}><strong>v{item.revision}</strong><span><b>{item.rulesCount} правил</b><small>{formatDate(item.publishedAt)} · {item.publishedByName || 'Система'}</small></span>{item.status === 'PUBLISHED' && <em>Активна</em>}</article>)}</section>}
    </div>}
  </div>;
}

function FilterIcon() {
  return <span className="routing-filter-symbol"><i /><i /><i /></span>;
}
